// Capacitor Native App Initialization
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { App } from '@capacitor/app';
import { Keyboard } from '@capacitor/keyboard';

// Check if running as native app
export const isNativeApp = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform(); // 'android', 'ios', or 'web'

// Initialize native features
export async function initializeApp() {
  if (!isNativeApp) {
    console.log('Running as web app (PWA)');
    return;
  }

  console.log(`Running as native ${platform} app`);

  try {
    // Configure Status Bar
    await StatusBar.setStyle({ style: Style.Light });
    await StatusBar.setBackgroundColor({ color: '#007897' });
  } catch (e) {
    console.log('StatusBar not available:', e);
  }

  try {
    // Hide splash screen after app is ready
    await SplashScreen.hide({
      fadeOutDuration: 500
    });
  } catch (e) {
    console.log('SplashScreen not available:', e);
  }

  // Handle app state changes
  App.addListener('appStateChange', ({ isActive }) => {
    console.log('App state changed. Is active?', isActive);
  });

  // Handle back button on Android
  App.addListener('backButton', ({ canGoBack }) => {
    if (!canGoBack) {
      App.exitApp();
    } else {
      window.history.back();
    }
  });

  // Handle keyboard on iOS/Android
  if (platform !== 'web') {
    Keyboard.addListener('keyboardWillShow', (info) => {
      console.log('Keyboard will show with height:', info.keyboardHeight);
    });

    Keyboard.addListener('keyboardWillHide', () => {
      console.log('Keyboard will hide');
    });
  }
}

// Open external links in system browser
export async function openExternalLink(url) {
  if (isNativeApp) {
    const { Browser } = await import('@capacitor/browser');
    await Browser.open({ url });
  } else {
    window.open(url, '_blank');
  }
}

// Haptic feedback (for native apps)
export async function hapticFeedback(type = 'light') {
  if (!isNativeApp) return;
  
  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    switch (type) {
      case 'light':
        await Haptics.impact({ style: ImpactStyle.Light });
        break;
      case 'medium':
        await Haptics.impact({ style: ImpactStyle.Medium });
        break;
      case 'heavy':
        await Haptics.impact({ style: ImpactStyle.Heavy });
        break;
      default:
        await Haptics.impact({ style: ImpactStyle.Light });
    }
  } catch (e) {
    console.log('Haptics not available:', e);
  }
}

export default {
  isNativeApp,
  platform,
  initializeApp,
  openExternalLink,
  hapticFeedback
};
